import { useNavigate } from 'react-router-dom';
import { Briefcase, Building2, Users, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const ROLES = [
  {
    id: 'gestion',
    icon: Briefcase,
    title: 'Gestión Interna del Servicio',
    description:
      'Personal de Valoriza: Jefatura, Operaciones, TI y Comunicación. Acceso completo a todas las plataformas y módulos del contrato.',
    route: '/gestion',
    accent: '#0A2540',
    bgAccent: 'rgba(10,37,64,0.06)',
    protected: true,
    badge: 'Valoriza',
  },
  {
    id: 'tecnicos',
    icon: Building2,
    title: 'Técnicos Municipales',
    description:
      'Servicios Técnicos del Ayuntamiento (STM). Supervisión del servicio, indicadores de calidad, informes y ecoparque.',
    route: '/tecnicos',
    accent: '#1E5AA8',
    bgAccent: 'rgba(30,90,168,0.06)',
    protected: true,
    badge: 'Ayuntamiento',
  },
  {
    id: 'ciudadania',
    icon: Users,
    title: 'Ciudadanía',
    description:
      'Vecinos de Los Alcázares y usuarios del Ecoparque. Servicios, incidencias, información del reciclaje y canales de contacto.',
    route: '/ciudadania',
    accent: '#1E5AA8',
    bgAccent: 'rgba(125,185,232,0.10)',
    protected: false,
    badge: 'Acceso libre',
  },
];

export default function RoleSelector() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleRoleClick = (roleItem) => {
    navigate(roleItem.route);
  };

  return (
    <section
      id="acceso"
      data-testid="role-selector"
      className="py-24 px-6 bg-[#FAFCFE]"
      aria-labelledby="acceso-heading"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full text-xs font-medium tracking-widest uppercase bg-[#CFE3F5] text-[#1E5AA8] mb-4">
            Elige tu acceso
          </span>
          <h2 className="font-display text-4xl sm:text-5xl text-[#0A2540] mt-3">
            ¿Quién eres?
          </h2>
          <p className="mt-4 text-[#6F8AA8] text-base max-w-xl mx-auto leading-relaxed">
            Cada perfil tiene acceso a las herramientas que necesita. Selecciona tu área para continuar.
          </p>
        </div>

        {/* Role cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
          {ROLES.map((role) => {
            const Icon = role.icon;
            return (
              <button
                key={role.id}
                data-testid={`role-card-${role.id}`}
                onClick={() => handleRoleClick(role)}
                className="group text-left rounded-3xl bg-white border border-[#CFE3F5] p-8 sm:p-10 hover:scale-[1.02] transition-all duration-200 shadow-sm hover:shadow-md hover:border-[rgba(30,90,168,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E5AA8] focus-visible:ring-offset-2"
                aria-label={`Acceder como ${role.title}`}
              >
                {/* Icon */}
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                  style={{ background: role.bgAccent }}
                >
                  <Icon
                    size={26}
                    style={{ color: role.accent }}
                    aria-hidden="true"
                  />
                </div>

                {/* Badge */}
                <span
                  className="inline-block px-3 py-1 rounded-full text-xs font-medium mb-4"
                  style={{
                    background: role.bgAccent,
                    color: role.accent,
                    border: `1px solid ${role.accent}22`,
                  }}
                >
                  {role.badge}
                </span>

                {/* Title */}
                <h3
                  className="font-display text-3xl mb-3 leading-tight"
                  style={{ color: '#0A2540' }}
                >
                  {role.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-[#6F8AA8] leading-relaxed mb-8">
                  {role.description}
                </p>

                {/* CTA */}
                <span
                  className="inline-flex items-center gap-2 text-sm font-medium"
                  style={{ color: role.accent }}
                >
                  Entrar
                  <ArrowRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                    aria-hidden="true"
                  />
                </span>

                {role.protected && (
                  <p className="mt-3 text-xs text-[#6F8AA8] flex items-center gap-1">
                    <span aria-hidden="true">🔒</span> Requiere autenticación
                  </p>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
